import { useId } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import type { SuccessBodyQuizSetDetailsRes } from '@/api/gen/_model/successBodyQuizSetDetailsRes';
import { useIsLoggerUserInfoSet } from '@/lib/auth';
import { NextQuizset } from '@/shared/components';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { cn } from '@/utils/tailwind.util';
import { hasFlashcardCards } from '../../../flashcard/utils';
import { useAnswerVisibility } from '../../../stores/AnswerVisibilityProvider';
import { useExportWorksheetHandler } from '../../cta-buttons/hooks/useExportWorksheetHandler';
import { useFlashcardHandler } from '../../cta-buttons/hooks/useFlashcardHandler';
import { LearningMaterialCard } from '../../learning-material-card/components/LearningMaterialCard';
import { MobileQuickActions } from '../../quick-actions/components';
import { QuizMetadataBar } from '../../quiz-metadata';
import { AnswerVisibilityToggle } from './AnswerVisibilityToggle';
import { GClassRoomShareButton } from './GClassRoomShareButton';
import { LoginContentGuardDescription, LoginContentGuardGradient } from './LoginContentGuard';
import { PremiumMapBanner } from './PremiumMapBanner';
import { QuizItemCard } from './QuizItemCard';
import { TrackedAnswerVisibilityToggle } from './TrackedAnswerVisibilityToggle';
interface QuizContentSectionProps {
  quizSetWithQuizList: SuccessBodyQuizSetDetailsRes;
  displayQuizList: QuizSetData[];
  shouldShowLoginGuard: boolean;
  onLinkToClassroom?: () => void;
}
export const QuizContentSection = ({
  quizSetWithQuizList,
  displayQuizList,
  shouldShowLoginGuard,
  onLinkToClassroom
}: QuizContentSectionProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const isInExternalPlatform = useIsInExternalPlatform();
  const {
    isAnswerHidden,
    changeAnswerVisibility
  } = useAnswerVisibility();
  const answerToggleId = useId();
  const quizSetId = quizSetWithQuizList.data.id;
  const isLoggerUserInfoSet = useIsLoggerUserInfoSet();
  const revealAnswers = () => {
    changeAnswerVisibility(true, 'first_card_notice');
    // 안내 버튼은 사라지므로 포커스를 전역 토글로 옮긴다. 토글이 화면 밖이어도 스크롤은 두어
    // 방금 드러난 정답을 보던 위치를 유지한다
    document.getElementById(answerToggleId)?.focus({
      preventScroll: true
    });
  };
  const answerToggleProps = {
    id: answerToggleId,
    checked: !isAnswerHidden,
    onCheckedChange: (isVisible: boolean) => changeAnswerVisibility(isVisible, 'global_toggle')
  };
  const handlePlanPurchaseClick = () => {
    void router.push('/pricing');
  };
  const handleExportWorksheet = useExportWorksheetHandler({
    quizData: quizSetWithQuizList.data
  });
  const handleFlashcard = useFlashcardHandler({
    quizId: quizSetWithQuizList.data.id,
    quizData: quizSetWithQuizList.data
  });
  const hasFlashcard = hasFlashcardCards(quizSetWithQuizList.data.quizList);
  return <div className={'flex flex-col gap-sm'} data-sentry-component="QuizContentSection" data-sentry-source-file="QuizContentSection.tsx">
      {quizSetWithQuizList.data.templatePlanType !== 'FREE' && <PremiumMapBanner onPlanPurchaseClick={handlePlanPurchaseClick} />}
      <QuizMetadataBar {...quizSetWithQuizList.data} countTrailing={
    // 로거에 유저 문맥이 실린 뒤에만 노출을 관측한다(토글은 그 전에도 정상 동작)
    isLoggerUserInfoSet ? <TrackedAnswerVisibilityToggle
    // 같은 탭에서 다른 퀴즈로 이동하면 다시 마운트해 노출을 새로 센다
    key={quizSetId} {...answerToggleProps} /> : <AnswerVisibilityToggle {...answerToggleProps} />} data-sentry-element="QuizMetadataBar" data-sentry-source-file="QuizContentSection.tsx" />
      {!isInExternalPlatform && <MobileQuickActions className="tablet-lg:hidden" onFlashcard={hasFlashcard ? handleFlashcard : undefined} onExportWorksheet={handleExportWorksheet} onLinkToClassroom={onLinkToClassroom} />}
      {quizSetWithQuizList.data.materials.map((material, index) => <LearningMaterialCard key={index} learningMaterial={material} />)}
      <section className={cn('flex flex-col gap-md relative', shouldShowLoginGuard && 'max-h-[600px] mobile-lg:max-h-[800px] overflow-hidden')}>
        {shouldShowLoginGuard && <LoginContentGuardGradient />}
        {displayQuizList.map((d, index) => <QuizItemCard data={{
        ...d,
        id: d.id || ''
      }} index={index} key={index} isAnswerHidden={isAnswerHidden} onRevealAnswers={revealAnswers} />)}
      </section>

      {shouldShowLoginGuard && <LoginContentGuardDescription quizCount={quizSetWithQuizList.data.quizList.length} />}

      {quizSetWithQuizList.data.isNextQuizsetAvailable && <section className="flex flex-col gap-2xs">
          <h6 className="text-body-md-bold text-neutral">
            {t('common:nextQuiz.label')}
          </h6>
          <NextQuizset quizsetId={quizSetWithQuizList.data.nextQuizsetId} />
        </section>}

      {!shouldShowLoginGuard && !isInExternalPlatform && <div className={'flex w-full justify-end'}>
          <GClassRoomShareButton entryMapHashId={quizSetWithQuizList?.data?.entryMapHashId || ''} />
        </div>}
    </div>;
};